import React, { useState } from 'react';
import { Col, Row, Spin, notification } from 'antd';
import { CalendarOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import UserHeader from '../../components/header/UserHeader';
import UserDrawer from '../../components/drawer/UserDrawer';
import goBackButton from '../../assets/images/Go_Back.svg';
import { WellnessCalendar, WellnessForm } from './components';
import { useWellnessEntries } from './hooks';
import './Wellness.css';

export const Wellness = () => {
   const navigate = useNavigate();
   const today = new Date();
   const [currentMonth, setCurrentMonth] = useState(today.getMonth());
   const [currentYear, setCurrentYear] = useState(today.getFullYear());
   const [selectedDate, setSelectedDate] = useState(null);
   const [isDrawerOpen, setIsDrawerOpen] = useState(false);

   const {
      entries,
      loading,
      currentEntry,
      formDraft,
      fetchEntries,
      getEntryByDate,
      createEntry,
      updateEntry,
      deleteEntry,
      saveDraft,
      selectEntry,
   } = useWellnessEntries(currentMonth, currentYear);

   const handleMonthChange = (month, year) => {
      setCurrentMonth(month);
      setCurrentYear(year);
   };

   const handleDateSelect = async (dateString, hasEntry) => {
      // Parse parts to avoid timezone shifts from new Date('YYYY-MM-DD')
      const [year, month, day] = dateString.split('-').map(Number);
      const selectedDateObj = new Date(year, month - 1, day);
      const todayObj = new Date();
      const todayStart = new Date(todayObj.getFullYear(), todayObj.getMonth(), todayObj.getDate());

      if (selectedDateObj > todayStart) {
         notification.error({ message: 'Error', description: 'You cannot add entries for future dates.' });
         return;
      }

      setSelectedDate(dateString);
      if (hasEntry) {
         await getEntryByDate(dateString);
      } else {
         selectEntry(null);
      }
   };

   const handleSave = async (id, data, isUpdate) => {
      if (isUpdate) {
         await updateEntry(id, data);
      } else {
         await createEntry(data);
      }
      await fetchEntries();
      if (selectedDate) {
         await getEntryByDate(selectedDate);
      }
   };

   const handleDelete = async id => {
      await deleteEntry(id);
      await fetchEntries();
      selectEntry(null);
   };

   return (
      <div className="wellness-page">
         <Row style={{ marginBottom: 20 }}>
            <Col span={24}>
               <UserHeader setDrawer={() => setIsDrawerOpen(true)} backgroundColor="#fafafa" />
            </Col>
            {isDrawerOpen && (
               <Col span={4}>
                  <UserDrawer isDrawerOpen={isDrawerOpen} onClose={() => setIsDrawerOpen(false)} />
               </Col>
            )}
         </Row>
         <button
            onClick={() => navigate('/landing-page')}
            className="text-left pl-4 button-styling"
            style={{ background: 'none', border: 'none', cursor: 'pointer' }}
         >
            <img src={goBackButton} alt="Go Back" />
         </button>

         <div className="wellness-container">
            <div className="wellness-header">
               <h1 className="wellness-title">Wellness Journal</h1>
               <p className="wellness-description">
                  Track your daily wellness, energy, sleep, and thoughts. Select a date to add or view entries.
               </p>
            </div>

            <div className="wellness-layout">
               <div className="wellness-sidebar">
                  <div className="wellness-card">
                     <div className="wellness-card-header">
                        <h3 className="wellness-card-title">Calendar</h3>
                     </div>
                     <div className="wellness-card-body">
                        <Spin spinning={loading}>
                           <WellnessCalendar
                              entries={entries}
                              selectedDate={selectedDate}
                              onDateSelect={handleDateSelect}
                              currentMonth={currentMonth}
                              currentYear={currentYear}
                              onMonthChange={handleMonthChange}
                           />
                        </Spin>
                     </div>
                     <div className="wellness-legend">
                        <div className="legend-item"><div className="legend-dot today" /><span>Today</span></div>
                        <div className="legend-item"><div className="legend-dot has-entry" /><span>Has Entry</span></div>
                        <div className="legend-item"><div className="legend-dot selected" /><span>Selected</span></div>
                     </div>
                  </div>
               </div>

               <div className="wellness-main">
                  <div className="wellness-card">
                     {selectedDate ? (
                        <div className="wellness-card-body">
                           <WellnessForm
                              onSave={handleSave}
                              onDelete={handleDelete}
                              entry={currentEntry}
                              selectedDate={selectedDate}
                              loading={loading}
                              formDraft={formDraft}
                              onDraftChange={saveDraft}
                           />
                        </div>
                     ) : (
                        <div className="wellness-empty-state">
                           <CalendarOutlined />
                           <h3>Select a Date</h3>
                           <p>Click on a date in the calendar to add a new entry or view an existing one.</p>
                        </div>
                     )}
                  </div>
               </div>
            </div>
         </div>
      </div>
   );
};

export default Wellness;
